
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface SearchFilters {
  selectedFilters: string[];
  distance: number[];
  rating: number[];
  dateRange: DateRange;
  availablePortions: number[];
  deliveryOnly: boolean;
  searchQuery: string;
}

export const useSearchFilters = () => {
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [distance, setDistance] = useState<number[]>([]);
  const [rating, setRating] = useState([0]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [cuisineTypes, setCuisineTypes] = useState<string[]>([]);
  const [dietaryTags, setDietaryTags] = useState<string[]>([]);
  const [allergens, setAllergens] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [availablePortions, setAvailablePortions] = useState([1]);
  const [deliveryOnly, setDeliveryOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const { data: cuisineData } = await supabase
          .from('cuisine_types')
          .select('name');
        if (cuisineData) {
          setCuisineTypes(cuisineData.map(c => c.name));
        }

        const { data: dietaryData } = await supabase
          .from('dietary_tags')
          .select('name');
        if (dietaryData) {
          setDietaryTags(dietaryData.map(d => d.name));
        }

        const { data: allergenData } = await supabase
          .from('allergens')
          .select('name');
        if (allergenData) {
          setAllergens(allergenData.map(a => a.name));
        }

        const savedSearches = localStorage.getItem('recentSearches');
        if (savedSearches) {
          setRecentSearches(JSON.parse(savedSearches).slice(0, 5));
        }
      } catch (error) {
        console.error('Error loading options:', error);
        toast({
          title: "Error",
          description: "Failed to load search options",
          variant: "destructive",
        });
      }
    };

    loadOptions();
  }, [toast]);

  const addToRecentSearches = (searchTerm: string) => {
    if (!searchTerm.trim()) return;
    
    const updatedSearches = [searchTerm, ...recentSearches.filter(s => s !== searchTerm)]
      .slice(0, 5);
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  const toggleFilter = (filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const resetFilters = () => {
    setDateRange({ from: undefined, to: undefined });
    setAvailablePortions([1]);
    setDeliveryOnly(false);
    setDistance([]);
    setRating([0]);
    setSelectedFilters([]);
    setSearchQuery("");
  };

  const handleDatePreset = (preset: 'today' | 'this-week' | 'next-week') => {
    const today = new Date();
    
    switch (preset) {
      case 'today': {
        const date = new Date(today);
        setDateRange({ 
          from: date, 
          to: date 
        });
        break;
      }
      case 'this-week': {
        const from = new Date(today);
        from.setHours(0, 0, 0, 0);
        const to = new Date(today);
        to.setDate(from.getDate() + (6 - from.getDay()));
        to.setHours(23, 59, 59, 999);
        setDateRange({ from, to });
        break;
      }
      case 'next-week': {
        const from = new Date(today);
        from.setDate(today.getDate() + (7 - today.getDay()));
        from.setHours(0, 0, 0, 0);
        const to = new Date(from);
        to.setDate(from.getDate() + 6);
        to.setHours(23, 59, 59, 999);
        setDateRange({ from, to });
        break;
      }
    }
  };

  return {
    showFilters,
    setShowFilters,
    distance,
    setDistance,
    rating,
    setRating,
    selectedFilters,
    setSelectedFilters,
    recentSearches,
    cuisineTypes,
    dietaryTags,
    allergens,
    dateRange,
    setDateRange,
    availablePortions,
    setAvailablePortions,
    deliveryOnly,
    setDeliveryOnly,
    searchQuery,
    setSearchQuery,
    addToRecentSearches,
    toggleFilter,
    resetFilters,
    handleDatePreset,
  };
};
